// substance/grouping.ts
import type { SubstanceRow } from "./_model";
import { USAGE_META, FAMILY_LABELS, FAMILY_COLORS, needsAttention } from "./_model";
export type GroupKey = "usage" | "none" | "code" | "cas" | "fema" | "family" | "tag";
export const GROUP_OPTIONS: { id: GroupKey; label: string }[] = [
  { id: "usage", label: "Usage" }, { id: "none", label: "None" }, { id: "code", label: "Code" },
  { id: "cas", label: "CAS" }, { id: "fema", label: "FEMA" }, { id: "family", label: "Aroma family" }, { id: "tag", label: "Tag" },
];
export interface SubstanceGroup {
  key: string; label: string | null; sublabel?: string | null;
  accent?: string | null; icon?: string | null; items: SubstanceRow[]; single: boolean; flat?: boolean;
}
export function buildGroups(items: SubstanceRow[], groupBy: GroupKey): SubstanceGroup[] {
  if (groupBy === "none") return [{ key: "__all", label: null, items, single: false, flat: true }];
  if (groupBy === "usage") {
    const order: (keyof typeof USAGE_META)[] = ["ingredient", "target", "formulation"];
    return order.map((u) => {
      const m = USAGE_META[u];
      return { key: u, label: m.label, accent: m.accent, icon: m.icon, sublabel: m.blurb,
               items: items.filter((s) => s.usage.includes(u)), single: false };
    }).filter((g) => g.items.length > 0);
  }
  if (groupBy === "tag") {
    const map = new Map<string, SubstanceRow[]>();
    items.forEach((s) => { (s.tags.length ? s.tags : ["Untagged"]).forEach((t) => { if (!map.has(t)) map.set(t, []); map.get(t)!.push(s); }); });
    return [...map.entries()]
      .sort((a, b) => (a[0] === "Untagged" ? 1 : b[0] === "Untagged" ? -1 : a[0].localeCompare(b[0])))
      .map(([k, arr]) => ({ key: k, label: k, items: arr, single: arr.length === 1 }));
  }
  const keyOf = (s: SubstanceRow): string => {
    switch (groupBy) {
      case "code": return s.code || "—"; case "cas": return s.cas[0] ?? "—";
      case "fema": return s.fema[0] ?? "—"; case "family": return s.family || "—"; default: return "—";
    }
  };
  const map = new Map<string, SubstanceRow[]>();
  items.forEach((s) => { const k = keyOf(s); if (!map.has(k)) map.set(k, []); map.get(k)!.push(s); });
  const entries = [...map.entries()].sort((a, b) =>
    a[0] === "—" ? 1 : b[0] === "—" ? -1 : String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }));
  return entries.map(([k, arr]) => {
    let label = k, sublabel: string | null = null, accent: string | null = null;
    if (groupBy === "family") { label = arr[0]?.familyLabel ?? FAMILY_LABELS[k] ?? k; accent = arr[0]?.familyColor ?? FAMILY_COLORS[k] ?? null; }
    if (groupBy === "cas") sublabel = "CAS"; if (groupBy === "fema") sublabel = "FEMA"; if (groupBy === "code") sublabel = "Code";
    return { key: k, label, sublabel, accent, items: arr, single: arr.length === 1 };
  });
}
export function groupAgg(items: SubstanceRow[]) {
  const compounds = items.filter((s) => s.type === "compound").length;
  return { count: items.length, needs: items.filter(needsAttention).length,
           inStock: items.filter((s) => s.stock?.inStock).length, withStock: items.filter((s) => s.stock).length,
           compounds, mixtures: items.length - compounds };
}
